import { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Cluster, Text, Field, Input, Textarea, Select, Button, Badge, Banner, Skeleton, Tabs, cx, type TabItem } from '../../ui';
import {
  NOTIF_EVENTS, eventDef, allowedTokenKeys, defaultTemplate,
  fetchTemplateOverrides, saveTemplate, resetTemplate, templateKey,
  renderTemplate, renderEmailBodyHtml, buildTokenValues, samplePayload,
  type TemplateRow, type NotifyEvent, type NotifyChannel,
} from '../../../lib/notifications-admin';
import { estimateSms } from '../../../lib/sms-segments';
import { supabase } from '../../../lib/supabase';

type FocusField = 'subject' | 'preview' | 'body' | 'cta';

// Which token keys a template string references (ignoring section markers).
function usedTokens(s: string): string[] {
  const keys: string[] = [];
  const re = /\{\{[#^/]?(\w+)\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) keys.push(m[1]);
  return keys;
}

export function TemplatesTab() {
  const [overrides, setOverrides] = useState<Map<string, TemplateRow> | null>(null);
  const [event, setEvent] = useState<NotifyEvent>('outbid');
  const [channel, setChannel] = useState<NotifyChannel>('email');
  const [variant, setVariant] = useState<string>('default');

  const [subject, setSubject] = useState('');
  const [preview, setPreview] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [testTo, setTestTo] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  const focusRef = useRef<{ field: FocusField; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);

  const def = eventDef(event)!;
  const ctaTarget = defaultTemplate(event, 'email', variant).cta_target;

  useEffect(() => {
    let alive = true;
    fetchTemplateOverrides()
      .then((m) => { if (alive) setOverrides(m); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load templates.'); });
    return () => { alive = false; };
  }, []);

  // When event changes, snap the variant to a valid one for that event.
  useEffect(() => {
    const vs = eventDef(event)!.variants;
    if (!vs.some((v) => v.variant === variant)) setVariant(vs[0].variant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  // Load the draft fields for the current (event, channel, variant) from the
  // override if one exists, else the shipped default.
  useEffect(() => {
    if (!overrides) return;
    const row = overrides.get(templateKey(event, channel, variant));
    const d = defaultTemplate(event, channel, variant);
    setSubject(row?.subject ?? d.subject ?? '');
    setPreview(row?.preview ?? d.preview ?? '');
    setBody(row?.body ?? d.body ?? '');
    setCtaLabel(row?.cta_label ?? d.cta_label ?? '');
    setSaved(false);
    setError(null);
  }, [overrides, event, channel, variant]);

  const hasOverride = overrides?.has(templateKey(event, channel, variant)) ?? false;

  // Validation: tokens must be in the event's whitelist.
  const allowed = allowedTokenKeys(event);
  const invalidTokens = useMemo(() => {
    const fields = channel === 'email' ? [subject, preview, body, ctaLabel] : [body];
    const bad = new Set<string>();
    for (const f of fields) for (const k of usedTokens(f)) if (!allowed.has(k)) bad.add(k);
    return [...bad];
  }, [channel, subject, preview, body, ctaLabel, allowed]);

  // Live preview values.
  const values = buildTokenValues(samplePayload(variant), window.location.origin, 'Kate');
  const smsEstimate = channel === 'sms' ? estimateSms(renderTemplate(body || '', values)) : null;

  const dirty = (() => {
    if (!overrides) return false;
    const row = overrides.get(templateKey(event, channel, variant));
    const d = defaultTemplate(event, channel, variant);
    const src = {
      subject: row?.subject ?? d.subject ?? '',
      preview: row?.preview ?? d.preview ?? '',
      body: row?.body ?? d.body ?? '',
      cta_label: row?.cta_label ?? d.cta_label ?? '',
    };
    return channel === 'email'
      ? src.subject !== subject || src.preview !== preview || src.body !== body || src.cta_label !== ctaLabel
      : src.body !== body;
  })();

  const blockSave = invalidTokens.length > 0 || (smsEstimate?.encoding === 'ucs2') || !body.trim()
    || (channel === 'email' && !subject.trim());

  function insertToken(key: string) {
    const token = `{{${key}}}`;
    const f = focusRef.current;
    if (!f) { // default: append to body
      setBody((b) => b + token);
      return;
    }
    const el = f.el;
    const start = el.selectionStart ?? el.value.length;
    const endPos = el.selectionEnd ?? el.value.length;
    const setter = { subject: setSubject, preview: setPreview, body: setBody, cta: setCtaLabel }[f.field];
    const cur = { subject, preview, body, cta: ctaLabel }[f.field];
    const next = cur.slice(0, start) + token + cur.slice(endPos);
    setter(next);
    requestAnimationFrame(() => { el.focus(); const p = start + token.length; el.setSelectionRange(p, p); });
  }

  const trackFocus = (field: FocusField) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    focusRef.current = { field, el: e.currentTarget };
  };

  async function save() {
    if (blockSave || saving) return;
    setSaving(true); setError(null); setSaved(false);
    const row: TemplateRow = {
      event_type: event, channel, variant,
      subject: channel === 'email' ? subject : null,
      preview: channel === 'email' ? preview : null,
      body,
      cta_label: channel === 'email' && ctaTarget ? ctaLabel : null,
    };
    try {
      await saveTemplate(row);
      setOverrides((m) => { const n = new Map(m); n.set(templateKey(event, channel, variant), row); return n; });
      setSaved(true);
    } catch (e: any) {
      setError(e?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (testBusy) return;
    setTestBusy(true); setTestMsg(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Sign in first.');
      const res = await fetch('/api/admin/notifications/test', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...(channel === 'email' ? { email: testTo.trim() || undefined } : { phone: testTo.trim() }),
          template: { event, variant, channel, subject, preview, body, cta_label: ctaLabel },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        setTestMsg(`Failed: ${data?.note || data?.detail || data?.error || res.status}`);
      } else {
        setTestMsg(`Sent ${channel === 'email' ? 'email' : 'SMS'} test${data?.note ? ` (${data.note})` : ''}.`);
      }
    } catch (e: any) {
      setTestMsg(e?.message ?? 'Test failed.');
    } finally {
      setTestBusy(false);
    }
  }

  async function reset() {
    setSaving(true); setError(null); setSaved(false);
    try {
      await resetTemplate(event, channel, variant);
      setOverrides((m) => { const n = new Map(m); n.delete(templateKey(event, channel, variant)); return n; });
    } catch (e: any) {
      setError(e?.message ?? 'Could not reset.');
    } finally {
      setSaving(false);
    }
  }

  const eventItems = NOTIF_EVENTS.map((e) => ({ value: e.event, label: e.label }));
  const channelItems: TabItem<NotifyChannel>[] = [{ value: 'email', label: 'Email' }, { value: 'sms', label: 'SMS' }];
  const variantItems: TabItem<string>[] = def.variants.map((v) => ({ value: v.variant, label: v.label }));

  return (
    <Stack gap="group">
      {/* Selectors */}
      <Cluster gap="group" align="end">
        <Field label="Event" className="w-[200px]">
          <Select value={event} onChange={(e) => setEvent(e.target.value as NotifyEvent)}>
            {eventItems.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </Select>
        </Field>
        <Tabs variant="segmented" items={channelItems} value={channel} onChange={setChannel} aria-label="Channel" />
        {hasOverride && <Badge tone="accent">Customised</Badge>}
      </Cluster>

      {def.variants.length > 1 && (
        <Tabs variant="segmented" items={variantItems} value={variant} onChange={setVariant} aria-label="Variant" />
      )}

      {error && <Banner tone="danger" title="Problem" onClose={() => setError(null)}>{error}</Banner>}
      {saved && !dirty && <Banner tone="success" title="Saved">Live within a minute.</Banner>}

      {!overrides ? (
        <Stack gap="group">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[35px] w-full" />)}</Stack>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* EDIT column */}
          <Stack gap="group">
            {channel === 'email' && (
              <>
                <Field label="Subject" error={!subject.trim() ? 'Subject is required.' : undefined}>
                  <Input value={subject} onFocus={trackFocus('subject')} onChange={(e) => { setSaved(false); setSubject(e.target.value); }} />
                </Field>
                <Field label="Inbox preview line" hint="The grey line shown after the subject in most inboxes.">
                  <Input value={preview} onFocus={trackFocus('preview')} onChange={(e) => { setSaved(false); setPreview(e.target.value); }} />
                </Field>
              </>
            )}

            <Field
              label={channel === 'email' ? 'Body' : 'Message'}
              hint={channel === 'email' ? 'Plain text. Blank line = new paragraph. **bold** for emphasis.' : 'One GSM-7 segment. Plain quotes and hyphens only.'}
              error={!body.trim() ? 'Message is required.' : undefined}
            >
              <Textarea
                value={body}
                onFocus={trackFocus('body')}
                onChange={(e) => { setSaved(false); setBody(e.target.value); }}
                className={cx(channel === 'email' ? 'min-h-[180px]' : 'min-h-[110px]')}
              />
            </Field>

            {channel === 'sms' && smsEstimate && (
              <Cluster gap="tight" align="center">
                <Badge tone={smsEstimate.encoding === 'ucs2' ? 'danger' : smsEstimate.segments > 1 ? 'warning' : 'success'}>
                  {smsEstimate.units}/{smsEstimate.perSegment} · {smsEstimate.segments} segment{smsEstimate.segments > 1 ? 's' : ''}
                </Badge>
                {smsEstimate.encoding === 'ucs2' && (
                  <Text role="caption" faint as="span">Contains {smsEstimate.badChars.join(', ')} - triples SMS cost. Replace to save.</Text>
                )}
              </Cluster>
            )}

            {channel === 'email' && ctaTarget && (
              <Field label="Button label" hint={`Links to ${ctaTarget === 'saleroom' ? 'the Saleroom' : 'the lot'} (destination is fixed).`}>
                <Input value={ctaLabel} onFocus={trackFocus('cta')} onChange={(e) => { setSaved(false); setCtaLabel(e.target.value); }} />
              </Field>
            )}

            {/* Variable chips */}
            <div>
              <Text role="label" muted as="p" className="mb-1.5">Insert a variable</Text>
              <Cluster gap="tight">
                {def.tokens.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => insertToken(t.key)}
                    title={t.label}
                    className="rounded-full border border-border bg-bone px-2.5 py-1 font-mono text-[0.6875rem] text-text-muted hover:border-text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  >
                    {`{{${t.key}}}`}
                  </button>
                ))}
              </Cluster>
            </div>

            {invalidTokens.length > 0 && (
              <Banner tone="warning" title="Unknown variable">
                {invalidTokens.map((k) => `{{${k}}}`).join(', ')} {invalidTokens.length > 1 ? 'are' : 'is'} not available for this event. Remove {invalidTokens.length > 1 ? 'them' : 'it'} to save.
              </Banner>
            )}

            <Cluster gap="tight">
              <Button variant="primary" onClick={save} loading={saving} disabled={!dirty || blockSave}>Save</Button>
              <Button variant="ghost" onClick={reset} disabled={!hasOverride || saving}>Reset to default</Button>
            </Cluster>

            {/* Per-template test send */}
            <div className="border-t border-border pt-3">
              <Text role="label" muted as="p" className="mb-1.5">Send a test</Text>
              <Cluster gap="tight" align="center">
                <Input
                  value={testTo}
                  onChange={(e) => { setTestMsg(null); setTestTo(e.target.value); }}
                  placeholder={channel === 'email' ? 'email (blank = your account email)' : 'phone, E.164 e.g. +15551234567'}
                  aria-label="Test recipient"
                  className="max-w-[300px]"
                />
                <Button variant="secondary" onClick={sendTest} loading={testBusy} disabled={channel === 'sms' && !testTo.trim()}>
                  Send test
                </Button>
              </Cluster>
              <Text role="caption" faint as="p" className="mt-1.5">
                Sends this {channel} with your current edits + sample data, prefixed [Test].
              </Text>
              {testMsg && <Text role="caption" as="p" className="mt-1">{testMsg}</Text>}
            </div>
          </Stack>

          {/* PREVIEW column */}
          <Stack gap="tight">
            <Text role="label" muted as="p">Preview (sample data)</Text>
            {channel === 'email' ? (
              <div className="overflow-hidden rounded-sm border border-border bg-[#FAFAF7] p-5">
                <Text role="caption" muted as="p" className="uppercase tracking-[0.14em]">Saleroom</Text>
                <Text role="body" as="p" className="mt-1 font-semibold">{renderTemplate(subject, values) || <span className="text-text-faint">(no subject)</span>}</Text>
                <Text role="caption" faint as="p" className="mb-3">{renderTemplate(preview, values)}</Text>
                <div className="border-t border-border pt-3" dangerouslySetInnerHTML={{ __html: renderEmailBodyHtml(body || '', values) }} />
                {ctaTarget && ctaLabel.trim() && (
                  <div className="mt-3 inline-block bg-ink px-5 py-2.5 font-body text-[0.75rem] font-bold uppercase tracking-[0.12em] text-bg">
                    {renderTemplate(ctaLabel, values)}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-bone p-4">
                <div className="max-w-[88%] rounded-2xl rounded-bl-sm bg-surface px-3.5 py-2.5 font-body text-small text-text shadow-[var(--shadow-soft)]">
                  {renderTemplate(body || '', values) || <span className="text-text-faint">(empty)</span>}
                </div>
                <Text role="caption" faint as="p" className="mt-2">+1 numbers get " Reply STOP to opt out." appended on the first message.</Text>
              </div>
            )}
          </Stack>
        </div>
      )}
    </Stack>
  );
}
